import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cartCount, setIsCartOpen } = useCart();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Workspace', href: '#' },
    { name: 'Hardware', href: '#' },
    { name: 'Bundles', href: '#' },
    { name: 'Support', href: '#' }
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/80 backdrop-blur-md border-b border-slate-200 py-2' : 'bg-white border-b border-transparent py-4'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <a href="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-indigo-200 group-hover:scale-110 transition-transform">
              <SafeIcon icon={FiIcons.FiAnchor} className="text-xl" />
            </div>
            <span className="font-bold text-xl tracking-tight text-slate-900">DeskHarbor</span>
          </a>
          
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href} 
                className="text-sm font-semibold text-slate-600 hover:text-indigo-600 transition-colors relative group"
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-indigo-600 transition-all group-hover:w-full"></span>
              </a>
            ))}
          </nav>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden md:flex items-center bg-slate-100 rounded-full px-3 py-1.5 border border-slate-200 focus-within:ring-2 focus-within:ring-indigo-500/20 transition-all">
            <SafeIcon icon={FiIcons.FiSearch} className="text-slate-400 text-sm" />
            <input 
              type="text" 
              placeholder="Search platform..." 
              className="bg-transparent border-none focus:ring-0 text-xs w-40 ml-2 text-slate-700 placeholder:text-slate-400"
            />
            <span className="text-[10px] font-mono text-slate-400 ml-2 px-1.5 py-0.5 border border-slate-200 rounded">⌘K</span>
          </div>

          <div className="flex items-center gap-1">
            <button className="p-2 text-slate-600 hover:bg-slate-100 rounded-full transition-colors" aria-label="Profile">
              <SafeIcon icon={FiIcons.FiUser} className="text-lg" />
            </button>
            <button 
              onClick={() => setIsCartOpen(true)}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-full transition-colors relative"
              aria-label={`Cart with ${cartCount} items`}
            >
              <SafeIcon icon={FiIcons.FiShoppingCart} className="text-lg" />
              {cartCount > 0 && (
                <motion.span 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-1.5 right-1.5 w-4 h-4 bg-indigo-600 text-white text-[9px] font-bold rounded-full border-2 border-white flex items-center justify-center"
                >
                  {cartCount}
                </motion.span>
              )}
            </button>
            <button 
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b border-slate-200 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 hover:text-indigo-600 rounded-md"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;